// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { ago } = require('shared/date')
const {
  TYPES: modelTypes,
  ARTICLE_SUBTYPES_KEYS: articleSubtypeKeys
} = require('shared/model-types')
// Client V2 common imports
const {
  CapsuleButton,
  CircleButton
} = require('client-v2/common/components/buttons')
const EmbeddedPlayer = require('client-v2/common/components/embedded-player')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const Icon = require('client-v2/common/containers/icons/component')
const ArticleMixButton = require('client-v2/common/containers/lightweight-engagement/components/article-mix-button')
const AddToCollectionModalAnchor = require('client-v2/common/containers/lightweight-engagement/containers/add-to-collection-modal/components/anchor-smart')
const { usePropsEventCallback } = require('client-v2/common/hooks/callbacks')
const useSymbol = require('client-v2/common/hooks/symbol')
const { articleDomainSearchHref } = require('client-v2/common/models/article/actions')
const { userProfileHref } = require('client-v2/common/models/user/actions')
const tag = require('client-v2/common/utils/hyperscript')
// Container imports
const { TabNameEnum } = require('../state/slide-record')
const ArticleProfileCollections = require('./collections-smart')
const ArticleProfileInterests = require('./interests-smart')

// BEM Block entity definition
const bem = BemEntity('ArticleProfileDetailCard')

function ArticleProfileDetailCard(props) {
  const { article, slideIndex } = props
  const articleMixButtonAnchorId = useSymbol()
  return (
    tag('article', {
      className: classNames(
        props.className,
        bem.block({
          // Prefer the subtype enum key in the stylesheet instead of the value
          subtype: articleSubtypeKeys[article.subtype]
        })
      )
    },
      tag(Curator, {
        time: props.curatedAt,
        user: props.curatedBy,
        onUserNavigate: props.onUserNavigate
      }),
      tag('div', {
        className: bem.element('contents')
      },
        tag('div', {
          className: bem.element('main-content')
        },
          tag('div', {
            className: bem.element('article-info')
          },
            tag(ArticleImage, props),
            tag(ArticleActions, {
              ...props,
              articleMixButtonAnchorId
            }),
            tag(ArticleDomain, props),
            tag(ArticleExcerpt, props),
            tag(RecReason, props)
          ),

          tag(Tabs, {
            ...props,
            articleMixButtonAnchorId
          })
        ),

        tag('div', {
          className: bem.element('sidebar-content')
        },
          tag(ArticleProfileCollections, {
            isScrollable: true,
            articleMixButtonAnchorId,
            slideIndex
          }),
          tag(ArticleProfileInterests, {
            slideIndex
          })
        )
      )
    )
  )
}

if (__DEV__) {
  // Prop checking imports
  const PropTypes = require('prop-types')
  const Portal = require('client-v2/common/components/portal')
  const { RecReasonRecord } = require('client-v2/common/containers/feed/records')

  const tabPropTypes = {
    count: PropTypes.number,
    isLoading: PropTypes.bool,
    isSelected: PropTypes.bool,
    label: PropTypes.string.isRequired
  }

  ArticleProfileDetailCard.propTypes = {
    // Values
    article: PropTypes.object.isRequired,
    articleMixButtonPortalContainer: Portal.containerPropType,
    className: PropTypes.string,
    curatedAt: PropTypes.string,
    curatedBy: PropTypes.object,
    recReason: PropTypes.shape(RecReasonRecord.propTypes),
    slideIndex: PropTypes.number,
    tabs: PropTypes.shape({
      [TabNameEnum.COLLECTIONS]: PropTypes.shape(tabPropTypes),
      [TabNameEnum.INTERESTS]: PropTypes.shape(tabPropTypes)
    }).isRequired,
    // Callbacks
    onAddClick: PropTypes.func,
    onContentClick: PropTypes.func,
    onDomainClick: PropTypes.func,
    onMoreActionsClick: PropTypes.func,
    onShareClick: PropTypes.func,
    onTabSelect: PropTypes.func,
    onUserNavigate: PropTypes.func
  }
}

module.exports = ArticleProfileDetailCard

// BEM Block entity Element components

function Curator(props) {
  const { time, user } = props
  const onClick = usePropsEventCallback(props, 'onUserNavigate')
  if (!user) {
    // Show the empty space where the curator should be
    return (
      tag('div', {
        className: bem.element('curator')
      })
    )
  }
  return (
    tag('div', {
      className: bem.element('curator')
    },
      tag(UserAvatar, {
        className: bem.element('curator-icon'),
        href: true,
        user,
        onClick
      }),
      tag('div', {
        className: bem.element('curator-text')
      },
        'First added by ',
        tag('a', {
          className: bem.element('curator-href'),
          href: userProfileHref(user),
          onClick
        },
          `@${user.username}`
        ),
        !!time && tag('time', {
          className: bem.element('curator-time'),
          dateTime: time
        },
          ago(time)
        )
      )
    )
  )
}

function ArticleImage(props) {
  // Props
  const { article } = props
  const { embedUrl, image, md5image, url } = article
  // Hooks
  let onImageClick = usePropsEventCallback(props, 'onContentClick')
  const [ isEmbedDisclosed, setEmbedDisclosed ] = React.useState(false)
  const onEmbedDisclose = React.useCallback(
    () => { setEmbedDisclosed(true) },
    [ setEmbedDisclosed ]
  )
  const onEmbedUndisclose = React.useCallback(
    () => { setEmbedDisclosed(false) },
    [ setEmbedDisclosed ]
  )
  // Render an embedded player if the article has an embed URL
  let embeddedPlayer
  if (embedUrl) {
    onImageClick = null
    embeddedPlayer = (
      tag(EmbeddedPlayer, {
        isDisclosed: isEmbedDisclosed,
        className: bem.element('embed'),
        src: embedUrl,
        onDisclose: onEmbedDisclose,
        onUndisclose: onEmbedUndisclose
      })
    )
  }
  return (
    tag('div', {
      className: bem.element('image-container')
    },
      tag(LazyImage, {
        className: bem.element('image'),
        href: url,
        rel: 'external nofollow noopener',
        target: '_blank',
        sources: [md5image, image],
        onClick: onImageClick
      }),
      embeddedPlayer
    )
  )
}

function ArticleActions(props) {
  const { article, articleMixButtonAnchorId: anchorId } = props
  const onAddClick = React.useCallback(
    () => { props.onAddClick({ article, anchorId }) },
    [ article, anchorId, props.onAddClick ]
  )
  const onContentClick = usePropsEventCallback(props, 'onContentClick')
  const onMoreActionsClick = usePropsEventCallback(props, 'onMoreActionsClick')
  const onShareClick = usePropsEventCallback(props, 'onShareClick')
  return (
    tag('div', {
      className: bem.element('actions')
    },
      tag(CapsuleButton, {
        className: bem.element('action-button', 'open'),
        href: article.url,
        rel: 'external nofollow noopener',
        target: '_blank',
        onClick: onContentClick
      },
        'Go to Website'
      ),
      /**
       * Hide "More Actions" button on your own posts because you can't report them.
       * @see https://app.asana.com/0/102902769366319/1111869557372309/f
       */
      !article.isMixed && (
        tag(CircleButton, {
          className: bem.element('action-button', 'more'),
          title: 'Show more actions for this post',
          onClick: onMoreActionsClick
        },
          tag(Icon, { id: 'vertical-ellipsis' })
        )
      ),
      tag('div', {
        className: bem.element('actions-spacer')
      }),
      tag(AddToCollectionModalAnchor, {
        placement: 'bottom-end',
        popperClassName: bem.element('add-button-popper'),
        portalContainer: props.articleMixButtonPortalContainer,
        urlId: props.article['url_id'],
        anchorId
      },
        tag(ArticleMixButton, {
          className: bem.element('action-button', 'mix'),
          checked: props.article.isMixed,
          onClick: onAddClick
        })
      ),
      tag(CircleButton, {
        className: bem.element('action-button', 'share'),
        title: 'Share this post with a friend',
        onClick: onShareClick
      },
        tag(Icon, { id: 'share-send' })
      )
    )
  )
}

function ArticleDomain(props) {
  const { article } = props
  const onClick = usePropsEventCallback(props, 'onDomainClick')
  return (
    tag('a', {
      className: bem.element('domain'),
      href: articleDomainSearchHref(article),
      title: article.hostname,
      onClick
    },
      article.hostname
    )
  )
}

function ArticleExcerpt(props) {
  const { article } = props
  const onClick = usePropsEventCallback(props, 'onContentClick')
  return (
    tag('a', {
      className: bem.element('excerpt'),
      href: article.url,
      rel: 'external nofollow noopener',
      target: '_blank',
      onClick
    },
      tag('h1', {
        className: bem.element('title')
      },
        article.title
      ),
      tag('p', {
        className: bem.element('description')
      },
        article.description
      )
    )
  )
}

function RecReason({ recReason }) {
  if (!recReason || !recReason.html) {
    return null
  }
  return (
    tag('div', {
      className: bem.element('rec-reason')
    },
      tag(RecReasonIcon, { recReason }),
      tag('span', {
        className: bem.element('rec-reason-text'),
        dangerouslySetInnerHTML: { __html: recReason.html }
      })
    )
  )
}

function RecReasonIcon({ recReason }) {
  const [ firstItem ] = recReason.items
  if (firstItem && firstItem.type === modelTypes.USER) {
    return (
      tag(UserAvatar, {
        className: bem.element('rec-reason-icon'),
        badge: false,
        href: true,
        user: firstItem
      })
    )
  }
  return null
}

function Tabs(props) {
  const {
    article,
    articleMixButtonAnchorId,
    slideIndex,
    tabs
  } = props
  const onSelect = React.useCallback(
    selectedTab => { props.onTabSelect({ article, selectedTab }) },
    [ article, props.onTabSelect ]
  )
  const renderTab = React.useCallback(
    name => tag(Tab, { ...tabs[name], name, onSelect }),
    [ tabs, onSelect ]
  )
  const renderSelectedTabContent = React.useCallback(
    () => {
      if (tabs[TabNameEnum.COLLECTIONS].isSelected) {
        return (
          tag(ArticleProfileCollections, {
            articleMixButtonAnchorId,
            slideIndex
          })
        )
      }
      if (tabs[TabNameEnum.INTERESTS].isSelected) {
        return tag(ArticleProfileInterests, { slideIndex })
      }
    },
    [ articleMixButtonAnchorId, slideIndex, tabs ]
  )
  return (
    tag('div', {
      className: bem.element('tabs-content')
    },
      tag('nav', {
        className: bem.element('nav')
      },
        tag('ul', {
          className: bem.element('tabs')
        },
          renderTab(TabNameEnum.COLLECTIONS),
          renderTab(TabNameEnum.INTERESTS)
        )
      ),
      renderSelectedTabContent()
    )
  )
}

function Tab(props) {
  const onClick = React.useCallback(
    () => { props.onSelect(props.name) },
    [ props.name, props.onSelect ]
  )
  // Hide the interests tab if there is nothing to show
  if (props.name === TabNameEnum.INTERESTS) {
    if (!props.isLoading && !props.count) {
      return null
    }
  }
  return (
    tag('li', {
      className: bem.element('tab', {
        [props.name]: true,
        interactive: !!onClick,
        loading: props.isLoading,
        selected: props.isSelected
      }),
      title: props.label,
      // Only allow tab selection once they have finished loading
      onClick: props.isLoading ? null : onClick
    },
      props.label
    )
  )
}
